import React, { useState, useEffect, useRef } from 'react'
import {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { RankingInfo, rankItem } from '@tanstack/match-sorter-utils'
import { Dropdown, Pagination, Table } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import threeDots from '../../../../_metronic/assets/images/svg/investor/threeDots.svg'
import TagManager from 'react-gtm-module'
import { DisplayImage } from '../../widgets/components/General/DisplayImage'
import AddTeamRoleModal from './AddTeamRoleModal'
import { deleteRole, getAllRoles } from '../core/_requests'
import { useAuth } from '../../auth'
import { TeamRole } from '../core/_models'
import { useTimeZone } from '../../../hooks/useTimeZone'
import { useDateFormat } from '../../../hooks/useDateFormat'
import { DateTime } from 'luxon'
import SkeletonRow from '../../digital-signing/components/SkeletonRow'
import NoDataPage from '../../widgets/components/NoDataPage'
import brandingConfig from '../../../../config/brandingConfig'
import { roleValues } from '../../../core/_constants'

declare module '@tanstack/react-table' {
  interface FilterFns {
    fuzzy: FilterFn<unknown>
  }
  interface FilterMeta {
    itemRank: RankingInfo
  }
}

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value)
  addMeta({ itemRank })
  return itemRank.passed
}

export const TeamRoleDataTable = () => {
  const { formatMessage } = useIntl()
  const [showModal, setShowModal] = useState(false)
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [globalFilter, setGlobalFilter] = useState('')
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>()
  const [, setShowKebabMenu] = useState(false)
  const { companyId, role } = useAuth()
  const [roleId, setRoleId] = useState<string>('')
  const [expandedYourShareholdings, setExpandedYourShareholdings] = useState<number | null>(null)
  const { getTimeZoneValue } = useTimeZone()
  const { getDateValue } = useDateFormat()

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  })

  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [allCompanyRoles, setAllCompanyRoles] = useState<TeamRole[]>([])
  const handleClickOutside = (event: any) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setShowKebabMenu(false)
    }
  }

  useEffect(() => {
    // Attach the event listener
    document.addEventListener('mousedown', handleClickOutside)

    // Cleanup the event listener on component unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const getAllCompanyRoles = async () => {
    try {
      setIsLoading(true)

      const {
        data: { success, data },
      } = await getAllRoles(+companyId!, true)
      if (success) {
        setAllCompanyRoles(
          data.roles.map((role: any, index: number) => {
            const hasName = role?.createdByUser?.firstName || role?.createdByUser?.lastName
            return {
              id: index,
              roleId: role?.roleId,
              roleName: role?.roleName,
              tagColor: role?.tagColor,
              createdAt: role?.createdAt,
              updatedAt: role?.updatedAt,
              createdByName: hasName
                ? `${role?.createdByUser?.firstName || ''} ${role?.createdByUser?.lastName || ''
                  }`.trim()
                : brandingConfig.brandName,
              createdByEmail: role?.createdByUser?.email || 'Default',
              usersCount: role?.userCount,
            }
          })
        )
      }
    } catch (err) {
      console.error('Error fetching folder or file list:', err)
    } finally {
      setShowModal(false)
      setIsLoading(false)
    }
  }
  useEffect(() => {
    if (companyId) {
      getAllCompanyRoles()
    }
  }, [companyId]) // eslint-disable-line react-hooks/exhaustive-deps
  const deleteCompanyRole = async () => {
    try {
      setIsLoading(true)
      const {
        data: { success },
      } = await deleteRole(+companyId!, roleId)
      if (success) {
        getAllCompanyRoles()
      }
    } catch (err) {
      console.error('Error fetching folder or file list:', err)
    } finally {
      setShowModal(false)
      setIsLoading(false)
    }
  }

  const handleYourRowToggle = (rowIndex: number) => {
    setExpandedYourShareholdings(expandedYourShareholdings === rowIndex ? null : rowIndex)
  }
  const hexToRGBA = (hex: string, opacity: number) => {
    let r = 0, g = 0, b = 0

    if (hex.length === 4) {
      r = parseInt(hex[1] + hex[1], 16)
      g = parseInt(hex[2] + hex[2], 16)
      b = parseInt(hex[3] + hex[3], 16)
    } else if (hex.length === 7) {
      r = parseInt(hex.slice(1, 3), 16)
      g = parseInt(hex.slice(3, 5), 16)
      b = parseInt(hex.slice(5, 7), 16)
    }

    return `rgba(${r}, ${g}, ${b}, ${opacity})`
  }
  const columns = React.useMemo<ColumnDef<TeamRole>[]>(
    () => [
      {
        accessorKey: 'roleName',
        id: 'roleName',
        header: 'Role Name',
        cell: (info) => {
          const value = info.getValue()
          const { tagColor } = info.row.original
          const messageId = typeof value === 'string' && value.trim() !== '' ? value : ' '

          const backgroundColor = hexToRGBA(tagColor || '#F56767', 0.25)

          return (
            <div className='d-flex align-items-center justify-content-start gap-2' >
              <div
                className='py-2 px-6 rounded text-nowrap overflow-hidden text-truncate'
                style={{ backgroundColor, color: tagColor, maxWidth: '300px', whiteSpace: 'nowrap' }}
                title={formatMessage({ id: messageId })}
              >
                {formatMessage({ id: messageId })}
              </div>
            </div>
          )
        },
        filterFn: 'includesString',
      },
      {
        accessorKey: 'name',
        id: 'name',
        header: 'Created By',
        cell: (info) => {
          const { createdByName, createdByEmail } = info.row.original
          return (
            <div className='d-flex align-items-center gap-2'>
              <DisplayImage
                imgName={''}
                alt={'profile-pic'}
                height={32}
                width={32}
                className='rounded-circle'
              />
              <div>
                <h5 className='m-0 font-size-13 fw-normal'>{createdByName}</h5>
                <span className='role-owner-color'>{createdByEmail}</span>
              </div>
            </div>
          )
        },
        filterFn: 'includesString',
      },
      {
        accessorKey: 'usersCount',
        id: 'usersCount',
        header: 'Users Assigned',
        cell: (info) => info.getValue(),
        filterFn: 'includesString',
      },
      {
        accessorKey: 'createdAt',
        id: 'createdAt',
        header: 'Created On',
        cell: (info) => {
          const dateString = (info.getValue() as string)?.toString()
          return (
            <>
              {dateString
                ? getDateValue(dateString)
                  ? getDateValue(dateString)
                  : DateTime.fromISO(dateString).toLocaleString(DateTime.DATE_MED)
                : '-'}{' '}
              {dateString && getTimeZoneValue(dateString)}
            </>
          )
        },
        filterFn: 'includesString',
      },
      {
        accessorKey: 'updatedAt',
        id: 'updatedAt',
        header: 'Last Updated On',
        cell: (info) => {
          const dateString = (info.getValue() as string)?.toString()
          return (
            <>
              {dateString
                ? getDateValue(dateString)
                  ? getDateValue(dateString)
                  : DateTime.fromISO(dateString).toLocaleString(DateTime.DATE_MED)
                : '-'}{' '}
              {dateString && getTimeZoneValue(dateString)}
            </>
          )
        },
        filterFn: 'includesString',
      },
    ], []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Function to handle clicks outside of the dropdown menu
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowKebabMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, []) //eslint-disable-line react-hooks/exhaustive-deps

  const table = useReactTable({
    data: allCompanyRoles as TeamRole[],
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnFilters,
      globalFilter,
      pagination,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    globalFilterFn: 'fuzzy',
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })
  const pageCount = Math.ceil(table.getPrePaginationRowModel().rows.length / pagination.pageSize)
  const currentPage = table.getState().pagination.pageIndex

  const handlePageChange = (pageIndex: number) => {
    table.setPageIndex(pageIndex)
  }

  return (
    <>
      <div className='d-flex flex-md-row flex-column gap-2 mb-4'>
        <div className='d-flex flex-md-row flex-column justify-content-between align-items-md-center align-items-start w-100 gap-1'>
          <p className='mb-0 fw-semibold'>{formatMessage({ id: 'User Roles' })}</p>
          <form className='align-items-center w-100 w-md-auto d-flex border border-2  rounded'>
            <span className='svg-icon svg-icon-3 position-absolute ms-3'>
              <svg
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                className='mh-50px'
              >
                <rect
                  opacity='0.5'
                  x='17.0365'
                  y='15.1223'
                  width='8.15546'
                  height='2'
                  rx='1'
                  transform='rotate(45 17.0365 15.1223)'
                  fill='currentColor'
                ></rect>
                <path
                  d='M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z'
                  fill='currentColor'
                ></path>
              </svg>
            </span>
            <DebouncedInput
              value={globalFilter ?? ''}
              onChange={(value) => {
                setGlobalFilter(String(value))
                if (String(value).length > 0) {
                  TagManager.dataLayer({
                    dataLayer: {
                      // event: GTMEvent.AdvisorMentorSearchSuccessful,
                      action: 'normal',
                      // metadata: {
                      //     company_id: companyId,
                      //     user_id: currentUser?.userId,
                      //     email: currentUser?.email,
                      //     search_value: value,
                      // },
                    },
                  })
                }
              }}
              className='bg-body form-control form-control-flush ps-10 rounded-2 font-size-13 font-weight-400 height-36'
              placeholder='Search'
            />
          </form>
        </div>
        <div className='d-flex align-items-center gap-1 flex-shrink-0'>
          <button
            className='btn text-white custom-button-property bg-primary height-36 d-flex align-items-center font-size-12 w-100 justify-content-center '
            onClick={() => { setShowModal(true); setRoleId('') }}
          >
            {formatMessage({ id: 'Add Role' })}
          </button>
          <AddTeamRoleModal
            showModal={showModal}
            setShowModal={setShowModal}
            roleId={roleId}
            setRoleId={setRoleId}
            getAllCompanyRoles={getAllCompanyRoles}
          />
        </div>
      </div>
      <div className='bg-transparent rounded-top' style={{ overflowX: 'auto' }}>
        {!isLoading && (allCompanyRoles?.length === 0 || table.getRowModel().rows.length === 0) ? (
          <div className='my-5 mx-6'>
            <NoDataPage width={'w-0'} bgTransparent />
          </div>
        ) : (
          <Table className='w-100 mb-0' style={{ minWidth: '600px' }}>
            <thead className='custom-thead border-bottom'>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className='font-size-12 font-weight-400 table-row border-dark-mode'
                >
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className='min-w-120px pb-6 pt-0 px-4'
                    >
                      {header.isPlaceholder ? null : (
                        <div
                          className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {{
                            asc: ' ▲',
                            desc: ' ▼',
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>
                      )}
                    </th>
                  ))}
                  <th className='min-w-120px pb-6 pt-4'>{formatMessage({ id: 'Actions' })}</th>
                </tr>
              ))}
            </thead>
            <tbody>
              {isLoading ? (
                <>
                  <SkeletonRow count={10} />
                </>
              ) : (
                table.getRowModel().rows.map((row, index) => (
                  <React.Fragment key={row.id}>
                    <tr
                      key={row.id}
                      onClick={() => {
                        handleYourRowToggle(index)
                      }}
                      className={`border-bottom`}
                    >
                      {row.getVisibleCells().map((cell, cellIndex) => (
                        <>
                          {cellIndex < 2 ? (
                            <td key={cell.id} className='pt-3 px-4 border-0'>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          ) : (
                            <td key={cell.id} className='pt-5 px-4 border-0'>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          )}
                        </>
                      ))}
                      <td
                        className='pt-5 px-4'
                        onClick={(e) => {
                          setRoleId(row.original.roleId)
                          e.stopPropagation()
                        }}
                      >
                        {row.original.roleName &&
                          row.original.roleName.toLowerCase() !== roleValues.OWNER &&
                          role === roleValues.OWNER && (
                            <div className='dropdown-container d-flex justify-content-center align-items-center pb-1'>
                              <Dropdown
                                show={index === selectedIndex}
                                onToggle={(isOpen, event) => {
                                  if (event.source === 'click') {
                                    setSelectedIndex(isOpen ? index : undefined)
                                  } else {
                                    setSelectedIndex(undefined)
                                  }
                                }}
                              >
                                <Dropdown.Toggle className='btn btn-white table-three-dots dots-container'>
                                  <img src={threeDots} alt='' className='three-dots-image' />
                                </Dropdown.Toggle>
                                <Dropdown.Menu className='m-2'>
                                  <Dropdown.Item
                                    className='text-dark ps-5'
                                    onClick={() => setShowModal(true)}
                                  >
                                    <p className='mb-0'>{formatMessage({ id: 'Edit' })}</p>
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    className='text-danger ps-5'
                                    onClick={() => deleteCompanyRole()}
                                  >
                                    {formatMessage({ id: 'Delete' })}
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </div>
                          )}
                      </td>
                    </tr>
                  </React.Fragment>
                ))
              )}
            </tbody>
          </Table>
        )}
      </div>
      <div
        className={`w-100 justify-content-between align-items-center px-4 py-4 bg-transparent rounded-bottom mb-8 ${allCompanyRoles.length < 10 ? 'd-none' : 'd-md-flex '
          }`}
      >
        <div className='d-flex gap-2 w-100 align-items-center justify-content-md-start justify-content-center mb-4 mb-md-0'>
          <select
            aria-label='Default select example'
            className='p-4 py-3 w-80px border border-1 rounded-2 font-size-13 bg-transparent text-dark '
            style={{ outline: 'none' }}
            value={pagination.pageSize}
            onChange={(e) => {
              const newSize = Number(e.target.value)
              setPagination((prev) => ({
                ...prev,
                pageSize: newSize,
                pageIndex: 0, // Reset to first page when page size changes
              }))
            }}
          >
            {[10, 20, 50, 100].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </select>

          <span className='font-size-13 text-center text-md-start'>
            {formatMessage({
              id: `Showing ${currentPage * pagination.pageSize + 1} to ${Math.min(
                (currentPage + 1) * pagination.pageSize,
                table.getPrePaginationRowModel().rows.length
              )} of ${table.getPrePaginationRowModel().rows.length} records`,
            })}
          </span>
        </div>

        {/* <Pagination className='w-md-100 w-auto d-md-flex flex-md-end'>
          <Pagination.First onClick={() => handlePageChange(0)} disabled={currentPage === 0}>
            {'<<'}
          </Pagination.First>

          <Pagination.Prev
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 0}
          >
            {'<'}
          </Pagination.Prev>

          {Array.from({length: pageCount}, (_, i) => (
            <Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>
              {i + 1}
            </Pagination.Item>
          ))}

          <Pagination.Next
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage >= pageCount - 1}
          >
            {'>'}
          </Pagination.Next>

          <Pagination.Last
            onClick={() => handlePageChange(pageCount - 1)}
            disabled={currentPage >= pageCount - 1}
          >
            {'>>'}
          </Pagination.Last>
        </Pagination> */}
        <Pagination className='w-md-100 w-auto d-md-flex flex-md-end'>
          {/* First Page Button */}
          <Pagination.First onClick={() => handlePageChange(0)} disabled={currentPage === 0}>
            {'<<'}
          </Pagination.First>

          {/* Previous Page Button */}
          <Pagination.Prev
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 0}
          >
            {'<'}
          </Pagination.Prev>

          {/* Page Number Buttons with Ellipsis */}
          {(() => {
            const pageNeighbours = 2 // Number of neighbouring pages to display
            const items = []
            const startPage = Math.max(0, currentPage - pageNeighbours)
            const endPage = Math.min(pageCount - 1, currentPage + pageNeighbours)

            // Always show the first page
            items.push(
              <Pagination.Item
                key={0}
                active={currentPage === 0}
                onClick={() => handlePageChange(0)}
              >
                1
              </Pagination.Item>
            )

            // Ellipsis before the current page range if there are skipped pages
            if (startPage > 1) {
              items.push(<Pagination.Ellipsis key='start-ellipsis' disabled />)
            }

            // Page numbers in the range of current page - pageNeighbours to current page + pageNeighbours
            for (let i = startPage; i <= endPage; i++) {
              items.push(
                <Pagination.Item
                  key={i}
                  active={currentPage === i}
                  onClick={() => handlePageChange(i)}
                >
                  {i + 1}
                </Pagination.Item>
              )
            }

            // Ellipsis after the current page range if there are skipped pages
            if (endPage < pageCount - 1) {
              items.push(<Pagination.Ellipsis key='end-ellipsis' disabled />)
            }

            // Always show the last page
            if (pageCount > 1) {
              items.push(
                <Pagination.Item
                  key={pageCount - 1}
                  active={currentPage === pageCount - 1}
                  onClick={() => handlePageChange(pageCount - 1)}
                >
                  {pageCount}
                </Pagination.Item>
              )
            }

            return items
          })()}

          {/* Next Page Button */}
          <Pagination.Next
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage >= pageCount - 1}
          >
            {'>'}
          </Pagination.Next>

          {/* Last Page Button */}
          <Pagination.Last
            onClick={() => handlePageChange(pageCount - 1)}
            disabled={currentPage >= pageCount - 1}
          >
            {'>>'}
          </Pagination.Last>
        </Pagination>
      </div>
    </>
  )
}
function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number
  onChange: (value: string | number) => void
  debounce?: number
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
  const [value, setValue] = useState(initialValue)

  useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value)
    }, debounce)

    return () => clearTimeout(timeout)
  }, [value, debounce, onChange])
  return <input {...props} value={value} onChange={(e) => setValue(e.target.value)} />
}
